import { useCallback } from "react";
import { useQueryFlags } from "./useQueryFlags";
import { useShareContext } from "services/ShareContext";
import { websiteHost } from "scripts/application/setting";

/**
 * Custom hook for sign-in related handlers
 * Provides reusable handlers for sign-in, create account, and social connection URLs
 */
export const useSignInHandlers = (userId: string = "") => {
  const { source } = useQueryFlags();
  const { listingDetailShareUrlRef } = useShareContext();

  let isInIframe = false;
  try {
    isInIframe = window.self !== window.top;
  } catch (e) {
    isInIframe = true;
  }

  if (!isInIframe && source === "listing-detail") {
    isInIframe = true;
  }

  /** Read the ref at invocation time so handlers always use the latest value */
  const getNextUrl = useCallback(() => {
    const shareUrl = listingDetailShareUrlRef.current;
    if (shareUrl && isInIframe) {
      try {
        const url = new URL(shareUrl);
        return `${websiteHost}${url.pathname}`;
      } catch {
        return window.location.href;
      }
    }
    return window.location.href;
  }, [listingDetailShareUrlRef, isInIframe]);

  const handleSignIn = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const nextUrl = getNextUrl();
      const loginUrl =
        "https://www.har.com/login?nexturl=" +
        encodeURIComponent(
          `${window.location.origin}/login/success?guest=${userId}&next_url=${nextUrl}`,
        ) +
        "#member";

      if (isInIframe) {
        try {
          if (window.top) {
            window.top.location.href = loginUrl;
          }
        } catch (e) {
          console.error("Cannot access parent window:", e);
          window.location.href = loginUrl;
        }
      } else {
        window.location.href = loginUrl;
      }
    },
    [getNextUrl, userId, isInIframe],
  );

  const handleCreateAccount = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const nextUrl = getNextUrl();
      const createAccountUrl =
        "https://www.har.com/login/createaccount?nexturl=" +
        encodeURIComponent(
          `${window.location.origin}/login/success?guest=${userId}&next_url=${nextUrl}`,
        );

      if (isInIframe) {
        try {
          if (window.top) {
            window.top.location.href = createAccountUrl;
          }
        } catch (e) {
          console.error("Cannot access parent window:", e);
          window.location.href = createAccountUrl;
        }
      } else {
        window.location.href = createAccountUrl;
      }
    },
    [getNextUrl, userId, isInIframe],
  );

  const handleSocialLogin = useCallback(
    (e: React.MouseEvent, connection: string) => {
      e.preventDefault();
      const computedNextUrl = getNextUrl();

      const nextUrl = `${
        window.location.origin
      }/login/success?guest=${userId}&next_url=${encodeURIComponent(computedNextUrl)}`;

      const redirectUri = encodeURIComponent(
        `https://www.har.com/login/social?nexturl=${encodeURIComponent(
          nextUrl,
        )}`,
      );

      const socialUrl = `https://har-auth.auth0.com/authorize?response_type=code&client_id=mZD0aRdYa38XXcmWyb8NpGsRaOhlcNJH&connection=${connection}&scope=openid+profile+email&redirect_uri=${redirectUri}`;

      if (isInIframe) {
        try {
          if (window.top) {
            window.top.location.href = socialUrl;
          }
        } catch (e) {
          console.error("Cannot access parent window:", e);
          window.location.href = socialUrl;
        }
      } else {
        window.location.href = socialUrl;
      }
    },
    [getNextUrl, userId, isInIframe],
  );

  return {
    handleSignIn,
    handleCreateAccount,
    handleSocialLogin,
  };
};
